<template>
  <div class="macros-page flex-1 min-h-0 flex flex-col p-4">
    <router-link
      :to="addAccountScoping('settings/macros/new')"
      class="button success button--fixed-top flex gap-1"
    >
      <fluent-icon icon="add-circle" />
      <span class="button__content">
        {{ $t('MACROS.HEADER_BTN_TXT') }}
      </span>
    </router-link>

    <div
      class="macros-page__body flex-1 min-h-0 flex flex-col"
      :class="{ 'macros-page__body--with-footer': showTableFooter }"
    >
      <div class="macros-header">
        <div class="macro-search-wrap">
          <div class="search-icon-wrap" @click="applySearch">
            <fluent-icon icon="search" class="search-icon" />
          </div>
          <input
            type="text"
            :placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
            class="macro-search-input"
            :value="searchQuery"
            @keyup.enter="applySearch"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <div class="w-[60%] macros-list-column flex-1 min-h-0 overflow-auto">
          <woot-loading-state
            v-if="uiFlags.isFetching || !hasLoadedOnce"
            :message="$t('MACROS.LOADING')"
          />
          <p
            v-else-if="!filteredRecords.length"
            class="macros-empty-state"
          >
            {{ emptyStateMessage }}
          </p>

          <table v-else class="woot-table">
            <thead>
              <th
                v-for="thHeader in $t('MACROS.LIST.TABLE_HEADER')"
                :key="thHeader"
              >
                {{ thHeader }}
              </th>
            </thead>
            <tbody>
              <macros-table-row
                v-for="macro in paginatedRecords"
                :key="macro.id"
                :macro="macro"
                @delete="openDeletePopup(macro)"
              />
            </tbody>
          </table>
        </div>

        <div class="w-[34%] macros-sidebar overflow-auto">
          <span v-dompurify-html="$t('MACROS.SIDEBAR_TXT')" />
        </div>
      </div>
    </div>

    <div
      v-if="showTableFooter"
      class="table-footer-wrap table-footer-sticky"
    >
      <table-footer
        :current-page="currentPage"
        :page-size="perPage"
        :total-count="filteredRecords.length"
        @page-change="onPageChange"
      />
    </div>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
      :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import accountMixin from 'dashboard/mixins/account.js';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import MacrosTableRow from './MacrosTableRow';

export default {
  components: {
    MacrosTableRow,
    TableFooter,
  },
  mixins: [alertMixin, accountMixin],
  data() {
    return {
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      loading: {},
      hasLoadedOnce: false,
      searchQuery: '',
      currentPage: 1,
      perPage: 20,
    };
  },
  computed: {
    ...mapGetters({
      records: ['macros/getMacros'],
      uiFlags: 'macros/getUIFlags',
    }),
    filteredRecords() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return this.records;
      }

      const publicLabel = this.$t(
        'MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL'
      ).toLowerCase();
      const privateLabel = this.$t(
        'MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL'
      ).toLowerCase();

      return this.records.filter(macro => {
        const visibility = macro.visibility || '';
        const visibilityLabel =
          visibility === 'global' ? publicLabel : privateLabel;

        return (
          String(macro.id).includes(query) ||
          (macro.name && macro.name.toLowerCase().includes(query)) ||
          (macro.created_by?.name &&
            macro.created_by.name.toLowerCase().includes(query)) ||
          (macro.updated_by?.name &&
            macro.updated_by.name.toLowerCase().includes(query)) ||
          visibility.toLowerCase().includes(query) ||
          visibilityLabel.includes(query)
        );
      });
    },
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRecords.slice(start, start + this.perPage);
    },
    emptyStateMessage() {
      if (this.searchQuery.trim()) {
        return this.$t('MACROS.SEARCH_404');
      }
      return this.$t('MACROS.LIST.404');
    },
    showTableFooter() {
      return (
        !this.uiFlags.isFetching &&
        this.hasLoadedOnce &&
        this.filteredRecords.length > 0
      );
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}?`;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.$store.dispatch('macros/get').finally(() => {
      this.hasLoadedOnce = true;
    });
  },
  methods: {
    onSearchInput(event) {
      this.searchQuery = event.target.value;
    },
    applySearch() {
      this.currentPage = 1;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    adjustPageAfterDataChange() {
      const maxPage = Math.max(
        1,
        Math.ceil(this.filteredRecords.length / this.perPage)
      );
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteMacro(this.selectedResponse.id);
    },
    async deleteMacro(id) {
      try {
        await this.$store.dispatch('macros/delete', id);
        this.showAlert(this.$t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const errorMessage =
          error.message || this.$t('MACROS.DELETE.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      } finally {
        this.loading[id] = false;
        this.adjustPageAfterDataChange();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';

.macros-page {
  overflow: hidden;
}

.macros-page__body {
  min-height: 0;

  &--with-footer {
    padding-bottom: 50px;
  }
}

.macros-list-column {
  min-height: 0;
}

.macros-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #64748b;

  @apply dark:text-slate-400;
}

.table-footer-wrap {
  flex-shrink: 0;
  min-width: 0;
}

.table-footer-sticky {
  position: fixed;
  right: 0;
  bottom: 0;
  width: calc(100vw - 3.5rem);
  z-index: 3;
  background: #f9f9f9;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);

  @apply dark:bg-slate-800 dark:border-slate-700;
}

.macros-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.macro-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  flex-shrink: 1;

  .search-icon-wrap {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    z-index: 1;
    color: #94a3b8;

    @apply dark:text-slate-400;

    :deep(svg) {
      height: 18px;
      width: 18px;
    }
  }

  .macro-search-input {
    width: 100%;
    height: 38px;
    padding: 0 40px 0 12px;
    font-size: 13px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: white;
    margin: 0;
    box-sizing: border-box;

    @apply dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200;

    &::placeholder {
      color: #9ca3af;
      @apply dark:text-slate-500;
    }

    &:focus {
      outline: none;
      border-color: #1f93ff;
      box-shadow: 0 0 0 1px #1f93ff;
    }
  }
}
</style>
